import { FEATURES } from '@constants/Text'
import { isCliente } from '@modules/financial-planning/utils/client-permissions'

/**
 * Gate de FEATURE FLAG do cliente final no Financial Planning.
 *
 * ★ Por que este arquivo existe separado de `client-permissions.js`:
 * o gate de PERFIL que fecha as 10 rotas da Parte A não pode conhecer flag
 * nenhuma. Enquanto `blockForCliente` não tiver como ler
 * `FINANCIAL_PLANNING_CLIENTE`, "desligar a flag não reabre a Parte A" é uma
 * propriedade estrutural do repositório e não uma promessa que depende do `if`
 * certo. Manter a leitura da flag num arquivo à parte torna a separação
 * verificável por `grep`, e não por leitura atenta.
 *
 * A dependência é de mão única e só nesta direção: aqui importamos `isCliente`
 * de lá; lá nada é importado daqui.
 */

/**
 * @param {object} show mapa de flags do escritório (`parameters/show`)
 * @returns {boolean} true quando o escritório tem a flag do cliente ligada
 */
export const hasFinancialPlanningCliente = (show) =>
  !!(show || {})[FEATURES.FINANCIAL_PLANNING_CLIENTE]

/**
 * `customPermission` das 3 rotas liberadas ao cliente final (`fp.home`,
 * `fp.transacoes`, `fp.objetivos`). **Decide os dois ramos**, cada um com a
 * sua flag:
 *
 * - **cliente** → `FINANCIAL_PLANNING_CLIENTE`, e só ela;
 * - **demais perfis** → `FINANCIAL_PLANNING_SIMULATOR`, exatamente como antes.
 *
 * ★ Por que o simulador mudou de endereço (RF-B39). As 3 rotas declaravam
 * `can_show: FINANCIAL_PLANNING_SIMULATOR` ao lado deste gate, e o guard global
 * aplica `can_show` a TODO perfil, antes e independentemente do
 * `customPermission` (`router/index.js:207-237` → `next({name:'404'})`). Com o
 * `can_show` no lugar, um escritório com a flag do cliente ligada e o módulo
 * profissional desligado mandava o cliente para o 404 nas três telas — enquanto
 * o menu, que lê só a flag do cliente (`navigation-routes.js:1006`), continuava
 * oferecendo as abas. A flag do cliente só decide sozinha se ninguém decidir
 * antes dela; por isso as duas condições passaram a morar no mesmo predicado.
 *
 * Regressão zero para o profissional: os três consumidores de `can_show`
 * (guard, `global-search/search-index.js:60`, `central-config/feature-gate.js:61`)
 * também avaliam `customPermission({ user, show })` com o MESMO mapa de flags —
 * `parameters/canShow(attr)` é literalmente `show[attr]` (`parameters/getters.js:58`).
 *
 * A assinatura recebe `{ user, show }` porque é o objeto que o guard global
 * monta (`router/index.js:292` → `routeTo.meta.customPermission({ user, show })`).
 *
 * @param {{user?: object, show?: object}} opts
 * @returns {boolean}
 */
export const allowClienteWithFlag = (opts) => {
  const user = (opts && opts.user) || {}
  const show = (opts && opts.show) || {}
  if (!isCliente(user)) return !!show[FEATURES.FINANCIAL_PLANNING_SIMULATOR]
  return hasFinancialPlanningCliente(show)
}

/**
 * O gate ÚNICO de leitura das 3 telas: quando `true`, a view esconde toda a
 * superfície de escrita e assume o layout do cliente.
 *
 * ★ Mora aqui, e não em `client-permissions.js`, apesar de o contrato original
 * tê-lo desenhado lá: aquele arquivo não pode importar
 * `FINANCIAL_PLANNING_CLIENTE`. É essa ausência que faz "desligar a flag não
 * reabre as rotas bloqueadas" ser verificável por `grep` em vez de por leitura
 * atenta. Trazer a flag para lá desmontaria a garantia inteira por uma questão
 * de endereço. O primitivo (`isCliente`) continua sendo o mesmo, importado de
 * lá — a dependência segue de mão única.
 *
 * São 14 ações mapeadas para suprimir nas três telas. Esconder uma a uma
 * garante que alguém esqueça uma, e superfície de escrita vazada é falha de
 * segurança de produto, não de estilo.
 *
 * @param {object} user usuário da sessão (`$currentUser()`)
 * @param {object} show mapa de flags do escritório (`$access`)
 * @returns {boolean}
 */
export const isReadOnlyClient = (user, show) =>
  isCliente(user) && hasFinancialPlanningCliente(show)
